import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBasket } from '~/features/basket/hooks';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import Flex from '~/shared/components/Flex';
import { Grid } from '~/shared/components/Grid';
import { Text } from '~/shared/components/Text/Text';
import { Checkbox, Form, Input } from '~/shared/components/form';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import {
    _matchCouponsFromCartWithProducts,
    _getCouponsFromOrder,
} from '~/shared/hooks/useEvents/useGTMEvents/helper';
import { useUtils } from '~/shared/hooks/useUtils';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useFrame } from '~/shared/utils/frame';
import { useTranslation } from '~/shared/utils/translation';
import {
    StyledCheckoutAside,
    StyledCheckoutMain,
    StyledCheckoutMobileSubmit,
    StyledCheckoutWrapper,
} from '~/templates/pages/components/CheckOutLayout/styled';
import { handleBrokerStartAgeVerification } from '~/services/mitId/ageVerification';
import { useTotalBonusEarning } from '~/services/special-price';
import Spacer from '~/theme/design-system/components/Spacer';
import { useCheckoutState } from '../../hooks/useCheckoutState';
import { useSubmit } from '../../hooks/useSubmit';
import { CustomerInformationInputs, Fields } from '../../models/fields';
import { useValidationSchema } from './lib/useValidationSchema';
import { ErrorMessage, StyledCreateAccountLabel, StyledGrid, StyledImageWrapper } from './styled';
import { Button, Image, Link } from '~/shared/components';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';
import { theme } from '~/theme';
import { ascertainPhoneNumber } from '~/shared/components/form/components/controllable-elements/phone-element/utils';
import { Phone } from '~/shared/components/form/components/form-fields/phone';
import { Defaults } from '../../models/checkout';
import { CheckoutSummary } from '../CheckoutSummary/CheckoutSummary';
import SectionTitle from '~/shared/components/Summary/components/SectionTitle/SectionTitle';
import { useCityByPostCode } from '~/shared/hooks/useCitySearch/useCityByPostCode';
import { useValidateEmailTld } from '../../hooks/useValidateEmailTld';
import { notifyError } from '~/shared/utils/errorBoundary/toast';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { AddressSuggestion } from '~/shared/hooks/useAddressAutocomplete/api/models';
import { toAddressLine } from '~/shared/hooks/useAddressAutocomplete/utils';
const OPERABLE_COUNTRY_CODES = ['DK'];

export const CustomerInformation = () => {
    const { translate } = useTranslation();
    const customer = useCustomer();
    const basket = useBasket();
    const { validate } = useValidateEmailTld();
    const { data: frame } = useFrame();
    const { beginCheckoutEvent } = useEvents(frame);
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { handlers } = useSubmit();
    const { products } = useProducts();
    const { membership, setMembership } = useLocalBasketState();
    const [localMembership, setLocalMembership] = useState(false);
    const [ageVerificationDetails, setAgeVerificationDetails] = useState({
        isAdult: false,
        isAgeVerified: false,
        errorDescription: '',
    });
    const hasCheckoutEventFiredRef = useRef(false);

    const { goForward, setBillingAddress, setZipCode } = useCheckoutState((store) => ({
        goForward: store.goForward,
        zipCode: store.zipCode,
        setZipCode: store.setZipCode,
        setBillingAddress: store.setBillingAddress,
    }));

    const validationSchema = useValidationSchema(OPERABLE_COUNTRY_CODES);

    const COOKIE_AREA_CODE = '_phone-area-code';

    const buttonRef = useRef<HTMLButtonElement>(null);
    const methods = useForm<CustomerInformationInputs>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            countryCode: localStorage.getItem(COOKIE_AREA_CODE) || OPERABLE_COUNTRY_CODES[0],
        },
    });

    const { convertPriceToLocale } = useUtils();
    const { totalBonusEarning, isReady: isBonusEarningReady } = useTotalBonusEarning();
    const consentValue = methods.watch(Fields.Consent);

    const handleConsent = (value?: boolean) => {
        methods.setValue(Fields.Consent, value ? value : !consentValue, { shouldValidate: true });
    };

    const handleSelectAddress = ({ data }: AddressSuggestion) => {
        const { postnr, postnrnavn } = data;

        methods.setValue(Fields.Address1, toAddressLine(data), { shouldValidate: true });
        methods.setValue(Fields.PostalCode, postnr || '');
        methods.setValue(Fields.City, postnrnavn || '');

        if (postnr) {
            methods.clearErrors(Fields.PostalCode);
        }
    };

    const handleAgeVerification = () => {
        const nonce = crypto.randomUUID();
        const promise = handleBrokerStartAgeVerification(nonce);

        promise.then((value) => {
            const idbrokerdkAgeVerified = value.claims?.idbrokerdk_age_verified;
            const isAdult = idbrokerdkAgeVerified?.includes('true') || false;
            const isAgeVerified = value.age_verified;
            const errorDescription = value.error_description ?? '';

            setAgeVerificationDetails({
                isAdult,
                isAgeVerified,
                errorDescription,
            });

            methods.setValue(Fields.AdultConfirm, isAdult, { shouldValidate: true });
        });
    };

    useEffect(() => {
        const gtmId = frame?.settings?.googleTagManagerId;
        if (basket.basketId && gtmId) {
            basket.updateBasketTrackingIds(basket.basketId);
        }
    }, [basket.basketId]);

    /**
     * Prefill form
     */
    useEffect(() => {
        if (customer.isInitialized) {
            if (basket.billingAddress) {
                const phoneNumber = ascertainPhoneNumber(basket.billingAddress.phone);

                if (basket?.customerInfo) {
                    const email = basket.customerInfo.email || customer.email || '';
                    methods.setValue(Fields.Email, email);
                    methods.setValue(Fields.ConfirmEmail, email);
                }

                methods.setValue(Fields.FirstName, String(basket.billingAddress.firstName));
                methods.setValue(Fields.LastName, String(basket.billingAddress.lastName));
                methods.setValue(Fields.Address1, basket.billingAddress.address1 ?? '');

                if (phoneNumber) {
                    methods.setValue(Fields.Phone, phoneNumber);
                }

                methods.trigger();
            } else if (customer?.isPlusMember) {
                const email = customer.email || basket?.customerInfo?.email || '';
                const phoneNumber = ascertainPhoneNumber(customer.phoneMobile);

                methods.setValue(Fields.Email, email);
                methods.setValue(Fields.ConfirmEmail, email);
                methods.setValue(Fields.FirstName, customer.firstName ?? '');
                methods.setValue(Fields.LastName, customer.lastName ?? '');

                if (customer?.addresses) {
                    const [address] = customer?.addresses;
                    methods.setValue(Fields.Address1, address.address1 ?? '');
                    methods.setValue(Fields.PostalCode, address.postalCode ?? '');
                }

                if (phoneNumber) {
                    methods.setValue(Fields.Phone, phoneNumber);
                }

                methods.trigger();
            }

            methods.setValue(
                Fields.CountryCode,
                localStorage.getItem(COOKIE_AREA_CODE) || OPERABLE_COUNTRY_CODES[0],
            );
        }
    }, [customer.isInitialized, customer.isPlusMember, customer.customerId]);

    useEffect(() => {
        if (customer.isInitialized && basket.billingAddress) {
            methods.setValue(Fields.PostalCode, basket.billingAddress.postalCode ?? '');
            methods.setValue(Fields.City, basket.billingAddress.city ?? '');
        }
    }, [customer.isInitialized]);

    useEffect(() => {
        if (!products?.length || hasCheckoutEventFiredRef.current) return;
        const couponCodes = _matchCouponsFromCartWithProducts(products, basket.couponItems);
        const orderCoupons = _getCouponsFromOrder(basket);

        beginCheckoutEvent(
            products.map((item, index) => {
                item.coupon = couponCodes?.appliedCoupons[item.id]?.couponCode;
                return _transformProductToDataLayer(item, item.quantity, index);
            }),
            String(basket.orderTotal),
            orderCoupons,
        );
        hasCheckoutEventFiredRef.current = true;
    }, [products]);

    useEffect(() => {
        const storedMembership = localStorage.getItem('membershipStatus');
        if (storedMembership !== null) {
            setLocalMembership(JSON.parse(storedMembership));
        }
    }, [membership]);

    const createAccount = methods.watch(Fields.CreateAccount);
    const zipCode = methods.watch(Fields.PostalCode);
    const { cityName, hasNoMatchingPostCode, isFetchingCityByPostCode } =
        useCityByPostCode(zipCode);
    const email = methods.watch(Fields.Email);
    const isValid = methods.formState.isValid;

    const saveInfo = async (payload: CustomerInformationInputs) => {
        if (!payload?.phoneMobile) {
            payload.phoneMobile = Defaults.phoneNumber;
        }

        localStorage.setItem(COOKIE_AREA_CODE, payload?.countryCode ?? OPERABLE_COUNTRY_CODES[0]);

        /* Also use this for shipping as default */
        await handlers.submitInformation(payload, true);

        setBillingAddress(payload);

        setZipCode(payload.postalCode);
    };

    const handleSubmit = async (payload: CustomerInformationInputs) => {
        validate(payload.email, {
            onSuccess: async (isValid) => {
                if (!isValid) {
                    notifyError(new Error(translate('checkout.errorTld')));
                    return;
                }
                if (payload.createAccount && payload.createAccountConsent) {
                    try {
                        await handlers.createAccount(payload);
                        payload[Fields.NewsLetterSignup] = true;
                    } catch (e) {
                        trackError(new Error(translate('createAccount.loginAlreadyInUse')), e);
                        methods.setValue(Fields.CreateAccount, false);
                        methods.setValue(Fields.CreateAccountConsent, false);
                        methods.setValue(Fields.ConfirmPassword, '');
                        methods.setValue(Fields.Password, '');
                        return;
                    }
                }

                await saveInfo(payload);

                goForward();
            },
        });
    };

    const autoSave = async () => {
        const payload = methods.getValues();
        await saveInfo(payload);
    };

    useEffect(() => {
        /**
         * If the form is valid, save it
         * if the user goes elsewhere, then the data is still there
         */

        if (isValid) {
            autoSave();
        }
    }, [isValid]);

    useEffect(() => {
        if (createAccount !== undefined) {
            setMembership(createAccount);
            localStorage.setItem('membershipStatus', JSON.stringify(createAccount));
        }
    }, [createAccount]);

    useEffect(() => {
        if (localMembership && !customer.isPlusMember) {
            methods.setValue(Fields.CreateAccount, localMembership);
        }
    }, [localMembership, customer.isPlusMember]);

    useEffect(() => {
        if (!zipCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (isFetchingCityByPostCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (cityName) {
            methods.setValue(Fields.City, cityName);
            methods.clearErrors(Fields.PostalCode);
            return;
        }

        if (hasNoMatchingPostCode) {
            methods.setError(Fields.PostalCode, {
                message: translate('message.postNumberValidationError.notFound'),
            });
            methods.setValue(Fields.City, '');
            methods.setValue(Fields.PostalCode, '');
        }
    }, [cityName, hasNoMatchingPostCode, isFetchingCityByPostCode, zipCode]);

    useEffect(() => {
        const { isDirty, isTouched } = methods.getFieldState(Fields.ConfirmEmail);

        if (isDirty && isTouched) methods.trigger(Fields.ConfirmEmail);
    }, [email]);

    return (
        <StyledCheckoutWrapper>
            <StyledCheckoutMain>
                <Flex column>
                    <Form methods={methods} onSubmit={handleSubmit}>
                        <SectionTitle>{translate('checkout.step1.headline')}</SectionTitle>

                        <Text as="h3" variant="display4"></Text>
                        <Spacer space="10px" />

                        <Grid flow="row" rowGap="extrasmall">
                            <Input
                                name={Fields.Email}
                                label={translate('forms.fields.form.mail')}
                                disabled={customer?.isPlusMember}
                                maxLength={50}
                                required
                                data-testid="form-email"
                            />
                            <Input type="hidden" name="useAsShipping" value="true"></Input>

                            {!customer?.isPlusMember && (
                                <>
                                    <Input
                                        name={Fields.ConfirmEmail}
                                        label={translate('forms.fields.form.mail.repeat')}
                                        maxLength={50}
                                        required
                                        data-testid="confirm-email"
                                    />

                                    <Checkbox
                                        name={Fields.CreateAccount}
                                        defaultChecked={localMembership}
                                        label={
                                            <StyledCreateAccountLabel>
                                                {translate(
                                                    'checkout.priceSection.signupLabel.part1',
                                                )}
                                                <span>
                                                    {translate(
                                                        'checkout.priceSection.signupLabel.part2',
                                                        {
                                                            amount: convertPriceToLocale(
                                                                Number(
                                                                    basket.c_totalMemberDiscount,
                                                                ),
                                                            ),
                                                        },
                                                    )}
                                                </span>
                                                <div>
                                                    {isBonusEarningReady &&
                                                        translate(
                                                            'checkout.priceSection.signupLabel.part3',
                                                            {
                                                                amount: convertPriceToLocale(
                                                                    totalBonusEarning,
                                                                ),
                                                            },
                                                        )}
                                                    <Spacer space="8px" />
                                                </div>
                                            </StyledCreateAccountLabel>
                                        }
                                    />

                                    {createAccount && (
                                        <>
                                            {/* TODO (IMC-2418):Forms: Input - Better handling of Help Text and Error Messages */}
                                            <Input
                                                name={Fields.Password}
                                                label={translate('forms.fields.form.password')}
                                                type="password"
                                                required
                                                helpText={translate('message.passwordHelpText')}
                                            />

                                            <Input
                                                name={Fields.ConfirmPassword}
                                                label={translate(
                                                    'forms.fields.form.password.repeat',
                                                )}
                                                type="password"
                                                required
                                            />

                                            <Checkbox
                                                name={Fields.CreateAccountConsent}
                                                label={
                                                    <>
                                                        {translate('forms.fields.form.concent1')}
                                                        <Spacer variant="horizontal" space="3px" />
                                                        <Link
                                                            target="_blank"
                                                            href={frame.market?.membershipTermsLink}
                                                        >
                                                            {
                                                                frame.market
                                                                    ?.membershipTermsConsentLabel
                                                            }
                                                        </Link>{' '}
                                                        og{' '}
                                                        <Link
                                                            target="_blank"
                                                            href={
                                                                frame.market?.personalDataPolicyLink
                                                            }
                                                        >
                                                            {frame.market?.personalDataPolicyLabel}
                                                        </Link>
                                                    </>
                                                }
                                            />
                                        </>
                                    )}
                                </>
                            )}

                            <StyledGrid flow="column" columns="1fr 1fr" columnGap="extrasmall">
                                <Input
                                    name={Fields.FirstName}
                                    label={translate('forms.fields.form.firstName')}
                                    maxLength={24}
                                    aria-label={`${translate('forms.fields.form.firstName')}, påkrævet`}
                                    required
                                    data-testid="first-name"
                                />

                                <Input
                                    name={Fields.LastName}
                                    maxLength={24}
                                    label={translate('forms.fields.form.lastName')}
                                    aria-label={`${translate('forms.fields.form.firstName')}, påkrævet`}
                                    required
                                    data-testid="last-name"
                                />
                            </StyledGrid>

                            <AddressAutocomplete
                                name={Fields.Address1}
                                label={translate('forms.fields.form.address1')}
                                maxLength={50}
                                aria-label={`${translate('forms.fields.form.address1')}, påkrævet`}
                                required
                                data-testid="address"
                                onSelectAddress={handleSelectAddress}
                            />

                            <StyledGrid flow="column" columns="1fr 2fr" columnGap="extrasmall">
                                <Input
                                    name={Fields.PostalCode}
                                    label={translate('forms.fields.form.postalcode')}
                                    maxLength={4}
                                    aria-label={`Postnummer, påkrævet`}
                                    required
                                    data-testid="postal-code"
                                />

                                <Input
                                    name={Fields.City}
                                    label={translate('forms.fields.form.city')}
                                    disabled
                                    required
                                />
                            </StyledGrid>

                            <Phone
                                name={Fields.Phone}
                                label={translate('forms.fields.form.mobile')}
                                countryCodeName={Fields.CountryCode}
                                operableCountryCodes={OPERABLE_COUNTRY_CODES}
                                maxCharacters={8}
                                messages={{
                                    inoperableCountryCode: translate(
                                        'message.inoperableCountryCode',
                                    ),
                                }}
                                required
                            />

                            <Text variant="caption">{translate('checkout.contactIf.label')}</Text>

                            <Spacer space="10px" />

                            <Checkbox
                                data-testid="order-requirements-checkbox"
                                name={Fields.Consent}
                                onChange={() => handleConsent()}
                                label={
                                    <>
                                        {translate('forms.fields.form.concent')}
                                        <Spacer variant="horizontal" space="3px" />
                                        <Link
                                            href={frame.market?.orderRequirementsLink}
                                            target="_blank"
                                        >
                                            {frame.market?.orderRequirementTitle}
                                        </Link>
                                    </>
                                }
                            />

                            <StyledCheckoutMobileSubmit
                                aria-label={translate('checkout.ctaLabel.step1')}
                                type="submit"
                                ref={buttonRef}
                                size="large"
                                disabled={!isValid}
                            >
                                {translate('checkout.ctaLabel.step1')}
                            </StyledCheckoutMobileSubmit>
                        </Grid>
                    </Form>

                    {basket?.c_adultPurchase && !ageVerificationDetails.isAdult && (
                        <>
                            <Button
                                aria-label={translate('checkout.confirmWithMitId')}
                                onClick={handleAgeVerification}
                                style={{ marginTop: theme.space[4] }}
                                size="large"
                            >
                                {translate('checkout.confirmWithMitId')}
                            </Button>
                            {!ageVerificationDetails.isAdult &&
                                ageVerificationDetails.isAgeVerified &&
                                ageVerificationDetails.errorDescription === '' && (
                                    <ErrorMessage aria-label={translate('checkout.isAdultDenied')}>
                                        {translate('checkout.isAdultDenied')}
                                    </ErrorMessage>
                                )}
                            {ageVerificationDetails.errorDescription === 'mitid_user_aborted' && (
                                <ErrorMessage aria-label={translate('checkout.mitIdRejected')}>
                                    {translate('checkout.mitIdRejected')}
                                </ErrorMessage>
                            )}
                        </>
                    )}

                    {basket?.c_adultPurchase && (
                        <>
                            <div>
                                <StyledImageWrapper>
                                    <Image
                                        src="/assets/sst-alkohol.png"
                                        width={100}
                                        height={100}
                                        sizes="(min-width: 1024px) 520px, 100vw"
                                        alt=""
                                    />
                                </StyledImageWrapper>
                            </div>
                            <Spacer />
                        </>
                    )}
                </Flex>
            </StyledCheckoutMain>

            <StyledCheckoutAside>
                <CheckoutSummary
                    ctaLabel={translate('checkout.ctaLabel.step1')}
                    onSubmit={() => buttonRef.current?.click()}
                    isCtaDisabled={!isValid}
                />
            </StyledCheckoutAside>
        </StyledCheckoutWrapper>
    );
};
